import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, Typography } from '@mui/material';
import { requestCertificatePrefill } from '../services/certificatePrefillService';
import { NUMBER_REQUIRED_ORGS } from '../constants/professionalRegistry';

const LABELS = { first_name: 'First and Middle Name', last_name: 'Last Name',
  registered_number: 'Division Number', certificate_expiry_date: 'Expiration Date' };

export default function CertificateAutofill({ file, registry, userid, locked, values, onApply }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [review, setReview] = useState(null);
  const [checked, setChecked] = useState({});
  const controller = useRef(null);
  const current = useRef({ values, registry, userid, locked });
  current.current = { values, registry, userid, locked };

  useEffect(() => {
    controller.current?.abort();
    setReview(null);
    setMessage('');
    setBusy(false);
    return () => controller.current?.abort();
  }, [file, registry, userid, locked]);

  const read = async () => {
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    const snapshot = { ...values };
    setBusy(true);
    setMessage('');
    try {
      const suggestions = await requestCertificatePrefill(file, registry, request.signal);
      if (request.signal.aborted) return;
      const rows = Object.entries(LABELS).flatMap(([key, label]) => {
        if (key === 'registered_number' && !NUMBER_REQUIRED_ORGS.includes(registry)) return [];
        const value = suggestions[key];
        if (typeof value !== 'string' || !value.trim() || value.length > 160) return [];
        if (key === 'certificate_expiry_date') {
          const parsed = new Date(`${value}T00:00:00Z`);
          if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) return [];
        }
        const numberLabel = registry === 'HKPCA' ? 'CCoun No.' : registry === 'MCHK' ? 'MCHK Registration No.' : label;
        return [{ key, label: key === 'registered_number' ? numberLabel : label, value: value.trim() }];
      });
      if (!rows.length) {
        setMessage('No clear details were found. You can enter them manually.');
        return;
      }
      setChecked(Object.fromEntries(rows.map(row => [row.key, !snapshot[row.key]])));
      setReview({ rows, snapshot, registry, userid });
    } catch (error) {
      if (!request.signal.aborted) setMessage(error.message);
    } finally {
      if (!request.signal.aborted) setBusy(false);
    }
  };

  const apply = async () => {
    const state = current.current;
    const rows = review.rows.filter(row => checked[row.key]);
    if (state.locked || state.registry !== review.registry || state.userid !== review.userid ||
      rows.some(row => (state.values[row.key] || '') !== (review.snapshot[row.key] || ''))) {
      setMessage('The form changed after extraction. Read the certificate again to review the latest details.');
      setReview(null);
      return;
    }
    setBusy(true);
    try {
      if (await onApply(Object.fromEntries(rows.map(row => [row.key, row.value])), review.snapshot)) {
        setReview(null);
        setMessage('Selected details applied. Check the fields and click Verify to verify your registration.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (!file) return null;
  return <Box sx={{ mt: 1 }}>
    <Button size="small" variant="outlined" onClick={read} disabled={busy || locked || !registry}>
      {busy ? 'Reading certificate…' : 'Read certificate with AI'}
    </Button>
    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
      {registry ? 'AI suggests details only. Review them before applying; registration still requires Verify.' : 'Select a registry to read the certificate.'}
    </Typography>
    {message && <Alert severity="info" sx={{ mt: 1 }}>{message}</Alert>}
    <Dialog open={Boolean(review)} onClose={() => !busy && setReview(null)} fullWidth maxWidth="sm" aria-labelledby="certificate-review-title">
      <DialogTitle id="certificate-review-title">Review certificate details</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Compare these suggestions with your certificate. Missing or unclear fields stay unchanged.
          Existing values are not selected for replacement. Applying an expiration date saves that date.
        </Typography>
        <Stack spacing={1}>
          {review?.rows.map(row => <Box key={row.key}>
            <FormControlLabel control={<Checkbox checked={Boolean(checked[row.key])} disabled={busy}
              onChange={event => setChecked(previous => ({ ...previous, [row.key]: event.target.checked }))} />}
              label={`${row.label}: ${row.value}`} />
            {review.snapshot[row.key] && <Typography variant="caption" display="block" sx={{ ml: 4 }}>Current: {review.snapshot[row.key]}</Typography>}
          </Box>)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={() => setReview(null)}>Cancel</Button>
        <Button disabled={busy || !review?.rows.some(row => checked[row.key])} onClick={apply}>Apply selected details</Button>
      </DialogActions>
    </Dialog>
  </Box>;
}
